var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var _a, _b;
import moment from 'moment';
import constants, { APPLICATION_TYPES } from '../../../config/constants';
import { FormActionIcon, FormFieldType, FormGroupVariant, FormLabelPlacement, FormSectionVariant, } from '../../../interfaces/createApplication';
import { businessNumberValidation, dateOfBirthRangeValidation, emailValidation, federalTaxIdValidation, firstNameOrLastNameValidation, formatTaxIdInput, formatSINInput, formatSSNInput, phoneNumberValidation, sanitizeNameInput, sinNumberValidation, ssnNumberValidation, } from '../../../utils/customerPortalValidations';
import { API_FIELD, BILLING_API_PREFIX } from './api_field_names';
import { amountValidation, SOLE_PROPRIETORSHIP_ENTITY_CODE, buildAddressFieldName, buildAddressFields, CANADA_COUNTRY_CODE, COUNTRY_OPTIONS, ENTITY_TYPE_OPTIONS, formatAmountInput, formatCurrencyDisplay, formatPercentageInput, formatPhoneInput, getStateOptions, getTitleOptions, parseAmount, percentageValidation, quantityValidation, SUFFIX_SELECT_OPTIONS, TAX_EXEMPT_RADIO_OPTIONS, yearValidation, YES_NO_RADIO_OPTIONS, } from './form_field_factories';
export var CUSTOMER_FIELD = {
    FedTaxId: 'fedTaxId',
    EntityType: 'entityType',
    LegalRegisteredName: 'legalRegisteredName',
    DbaName: 'dbaName',
    Country: 'country',
    StateOfIncorporation: 'stateOfIncorporation',
    DateBusinessIncorporated: 'dateBusinessIncorporated',
    GrossAnnualRevenue: 'grossAnnualRevenue',
    TaxExempt: 'taxExempt',
    VeteranStatus: 'veteranStatus',
    BillingSameAsMain: 'billingSameAsMain',
    SignerFirstName: 'signerFirstName',
    SignerMiddleName: 'signerMiddleName',
    SignerLastName: 'signerLastName',
    SignerSuffix: 'signerSuffix',
    SignerTitle: 'signerTitle',
    SignerEmail: 'signerEmail',
    SignerPhone: 'signerPhone',
    DocumentSignerSameAsGuarantor: 'documentSignerSameAsGuarantor',
    PercentOwnership: 'percentOwnership',
    BusinessTitle: 'businessTitle',
    TaxpayerId: 'taxpayerId',
    DateOfBirth: 'dateOfBirth',
};
export var CUSTOMER_ASPIRE_FIELD = {
    CustomerAspireId: 'customerAspireId',
    CustomerAspireOId: 'customerAspireOId',
    CustomerAspireRecordId: 'customerAspireRecordId',
    LocationAspireOId: 'locationAspireOId',
    BillToLocationAspireOId: 'billToLocationAspireOId',
    SignerContactAspireOId: 'signerContactAspireOId',
    IsFromExistingCustomer: 'isFromExistingCustomer',
};
export var CUSTOMER_LOOKUP_KEY = {
    ExistingCustomerByTaxId: 'existingCustomerByTaxId',
    ExistingSignerGuarantorByTaxId: 'existingSignerGuarantorByTaxId',
};
export var GUARANTOR_LOOKUP_KEY = {
    ExistingGuarantorByTaxId: 'existingGuarantorByTaxId',
};
export var GUARANTOR_SECTION_ID = {
    GuarantorType: 'guarantorTypeChoice',
    IndividualDetails: 'guarantorIndividualDetails',
    BusinessDetails: 'guarantorBusinessDetails',
    OwnershipAndAddress: 'guarantorOwnershipAndAddress',
    AuthorizedSigner: 'guarantorAuthorizedSigner',
};
export var CUSTOMER_SEARCH_KEY = {
    ExistingCustomerByLegalName: 'existingCustomerByLegalName',
};
export var CUSTOMER_SECTION_ID = {
    BusinessDetails: 'businessDetails',
    IndividualDetails: 'individualDetails',
    AddressDetails: 'addressDetails',
    BillingAddress: 'billingAddress',
    AuthorizedSigner: 'authorizedSigner',
    GuarantorAdditionalDetails: 'guarantorAdditionalDetails',
};
export var MAIN_ADDRESS_PREFIX = 'main';
export var BILLING_ADDRESS_PREFIX = 'billing';
export var GUARANTOR_ADDRESS_PREFIX = 'guarantorAddress';
var TODAY = moment().format(constants.US_DATE_FORMAT);
var FULL_WIDTH = { xs: 12, sm: 12, md: 12, lg: 12 };
var HALF_WIDTH = { xs: 12, sm: 12, md: 6, lg: 6 };
var THIRD_WIDTH = { xs: 12, sm: 6, md: 4, lg: 4 };
var LOCATION_OPTIONS = [];
var EQUIPMENT_TYPE_OPTIONS = [];
var FINANCE_PRODUCT_OPTIONS = [];
var LEGAL_REGISTERED_NAME_OPTIONS = [];
var CANADA_MARKER = 'can';
export var isCanadianProgram = function (programCountry, programName) {
    return (programCountry || programName).toLowerCase().includes(CANADA_MARKER);
};
var isCanadianCustomer = function (values, isCanadaProgram) {
    return isCanadaProgram || values[CUSTOMER_FIELD.Country] === CANADA_COUNTRY_CODE;
};
var isBillingSameAsMain = function (values) {
    return Boolean(values[CUSTOMER_FIELD.BillingSameAsMain]);
};
var isDocumentSignerAlsoGuarantor = function (values) {
    return Number(values[CUSTOMER_FIELD.DocumentSignerSameAsGuarantor]) === 1;
};
var isSoleProprietorshipEntityType = function (values) { var _a; return String((_a = values[CUSTOMER_FIELD.EntityType]) !== null && _a !== void 0 ? _a : '') === SOLE_PROPRIETORSHIP_ENTITY_CODE; };
var buildIndividualDetailsSection = function (isCanadaProgram, isVisible, isPrimaryCustomer) {
    if (isPrimaryCustomer === void 0) { isPrimaryCustomer = false; }
    return (__assign(__assign({ id: CUSTOMER_SECTION_ID.IndividualDetails, title: 'Individual Details', configSection: 'Individual Details', variant: FormSectionVariant.Filled }, (isVisible ? { isVisible: isVisible } : {})), { groups: [
            {
                id: 'individualDetailsFields',
                fields: [
                    {
                        name: CUSTOMER_FIELD.SignerFirstName,
                        apiFieldName: API_FIELD.FirstName,
                        label: 'First Name',
                        type: FormFieldType.Text,
                        isMandatory: true,
                        maxLength: 50,
                    },
                    {
                        name: CUSTOMER_FIELD.SignerMiddleName,
                        apiFieldName: API_FIELD.MiddleName,
                        label: 'Middle Name',
                        type: FormFieldType.Text,
                        isMandatory: false,
                        maxLength: 50,
                    },
                    {
                        name: CUSTOMER_FIELD.SignerLastName,
                        apiFieldName: API_FIELD.LastName,
                        label: 'Last Name',
                        type: FormFieldType.Text,
                        isMandatory: true,
                        maxLength: 50,
                    },
                    {
                        name: CUSTOMER_FIELD.SignerSuffix,
                        apiFieldName: API_FIELD.Suffix,
                        label: 'Suffix',
                        type: FormFieldType.Select,
                        isMandatory: false,
                        options: SUFFIX_SELECT_OPTIONS,
                    },
                    {
                        name: CUSTOMER_FIELD.BusinessTitle,
                        apiFieldName: API_FIELD.BusinessTitle,
                        label: 'Business Title',
                        type: FormFieldType.Text,
                        isMandatory: false,
                        maxLength: 50,
                    },
                    {
                        name: CUSTOMER_FIELD.TaxpayerId,
                        apiFieldName: API_FIELD.TaxpayerId,
                        label: 'SSN#/TIN#/SIN#',
                        type: FormFieldType.Text,
                        isMandatory: !isCanadaProgram,
                        maxLength: 11,
                        lookupKey: CUSTOMER_LOOKUP_KEY.ExistingCustomerByTaxId,
                        format: function (value) {
                            return isCanadaProgram ? formatSINInput(value) : formatSSNInput(value);
                        },
                        validate: function (value) {
                            return isCanadaProgram ? sinNumberValidation(value) : ssnNumberValidation(value);
                        },
                    },
                    {
                        name: CUSTOMER_FIELD.DateOfBirth,
                        apiFieldName: API_FIELD.DateOfBirth,
                        label: 'Date Of Birth (MM/DD/YYYY)',
                        type: FormFieldType.Date,
                        isMandatory: true,
                        maxDate: TODAY,
                        validate: function (value) { return dateOfBirthRangeValidation(value); },
                    },
                    {
                        name: CUSTOMER_FIELD.VeteranStatus,
                        apiFieldName: API_FIELD.VeteranStatus,
                        label: 'Is this customer a Veteran?',
                        type: FormFieldType.Radio,
                        isVisible: function () { return isPrimaryCustomer; },
                        isMandatory: false,
                        options: YES_NO_RADIO_OPTIONS,
                        labelPlacement: FormLabelPlacement.Above,
                        gridSize: FULL_WIDTH,
                    },
                ],
            },
        ] }));
};
var buildBusinessDetailsSection = function (isPrimaryCustomer, isCanadaProgram, isHidden) {
    if (isHidden === void 0) { isHidden = false; }
    return ({
        id: CUSTOMER_SECTION_ID.BusinessDetails,
        title: 'Business Details',
        configSection: 'Business Details',
        variant: FormSectionVariant.Filled,
        isVisible: function () { return !isHidden; },
        groups: [
            {
                id: 'businessDetailsFields',
                fields: [
                    {
                        name: CUSTOMER_FIELD.EntityType,
                        apiFieldName: API_FIELD.EntityType,
                        label: 'Entity Type',
                        type: FormFieldType.Select,
                        isMandatory: true,
                        options: ENTITY_TYPE_OPTIONS,
                        resetFields: [CUSTOMER_FIELD.SignerTitle],
                    },
                    {
                        name: CUSTOMER_FIELD.LegalRegisteredName,
                        apiFieldName: API_FIELD.LegalRegisteredName,
                        label: 'Legal Registered Name',
                        type: FormFieldType.AutoComplete,
                        isMandatory: true,
                        isFreeSolo: true,
                        options: LEGAL_REGISTERED_NAME_OPTIONS,
                        searchKey: CUSTOMER_SEARCH_KEY.ExistingCustomerByLegalName,
                        dependsOn: [CUSTOMER_FIELD.EntityType],
                    },
                    {
                        name: CUSTOMER_FIELD.DbaName,
                        apiFieldName: API_FIELD.DbaName,
                        label: 'DBA Name',
                        type: FormFieldType.Text,
                        isMandatory: false,
                        maxLength: 50,
                        tooltip: 'Doing Business As - the trade name this business operates under.',
                    },
                    {
                        name: CUSTOMER_FIELD.Country,
                        apiFieldName: API_FIELD.CustomerCountry,
                        label: 'Country',
                        type: FormFieldType.Select,
                        isMandatory: true,
                        options: COUNTRY_OPTIONS,
                        resetFields: [CUSTOMER_FIELD.StateOfIncorporation],
                    },
                    {
                        name: CUSTOMER_FIELD.StateOfIncorporation,
                        apiFieldName: API_FIELD.CustomerState,
                        label: 'State/Province Of Incorporation',
                        type: FormFieldType.Select,
                        isMandatory: true,
                        dependsOn: [CUSTOMER_FIELD.Country],
                        getOptions: function (values) { return getStateOptions(values[CUSTOMER_FIELD.Country]); },
                        isDisabled: function (values) { return !values[CUSTOMER_FIELD.Country]; },
                        disabledTooltip: 'Select Country to enable State',
                    },
                    {
                        name: CUSTOMER_FIELD.DateBusinessIncorporated,
                        apiFieldName: API_FIELD.DateBusinessIncorporated,
                        label: 'Date Business Incorporated',
                        type: FormFieldType.Date,
                        isMandatory: false,
                        maxDate: TODAY,
                    },
                    {
                        name: CUSTOMER_FIELD.FedTaxId,
                        apiFieldName: API_FIELD.TaxpayerId,
                        label: 'FED TAX ID/BN#',
                        getLabel: function (values) {
                            return isSoleProprietorshipEntityType(values)
                                ? 'FED TAX ID/BN#/SSN#'
                                : 'FED TAX ID/BN#';
                        },
                        type: FormFieldType.Text,
                        isMandatory: !isCanadaProgram,
                        maxLength: 15,
                        tooltip: 'Federal Tax ID (US) / Business Number (Canada).',
                        lookupKey: CUSTOMER_LOOKUP_KEY.ExistingCustomerByTaxId,
                        dependsOn: [CUSTOMER_FIELD.Country, CUSTOMER_FIELD.EntityType],
                        format: formatTaxIdInput,
                        validate: function (value, values) {
                            return isCanadianCustomer(values, isCanadaProgram)
                                ? businessNumberValidation(value)
                                : federalTaxIdValidation(value);
                        },
                    },
                    {
                        name: CUSTOMER_FIELD.GrossAnnualRevenue,
                        apiFieldName: API_FIELD.GrossRevenue,
                        label: 'Gross Annual Revenue',
                        type: FormFieldType.Text,
                        isMandatory: false,
                        maxLength: 16,
                        format: formatAmountInput,
                        validate: amountValidation,
                    },
                    {
                        name: CUSTOMER_FIELD.TaxExempt,
                        apiFieldName: API_FIELD.TaxExempt,
                        label: 'Tax Exempt',
                        type: FormFieldType.Radio,
                        isMandatory: true,
                        options: TAX_EXEMPT_RADIO_OPTIONS,
                        labelPlacement: FormLabelPlacement.Above,
                        gridSize: FULL_WIDTH,
                    },
                    {
                        name: CUSTOMER_FIELD.VeteranStatus,
                        apiFieldName: API_FIELD.VeteranStatus,
                        label: 'Is this customer a Veteran?',
                        type: FormFieldType.Radio,
                        isVisible: function (values) {
                            return isPrimaryCustomer && !isSoleProprietorshipEntityType(values);
                        },
                        dependsOn: [CUSTOMER_FIELD.EntityType],
                        isMandatory: false,
                        options: YES_NO_RADIO_OPTIONS,
                        labelPlacement: FormLabelPlacement.Above,
                        gridSize: FULL_WIDTH,
                    },
                ],
            },
        ],
    });
};
var addressDetailsSection = {
    id: 'addressDetails',
    title: 'Address Details',
    configSection: 'Address Details',
    variant: FormSectionVariant.Filled,
    groups: [
        {
            id: 'addressDetailsFields',
            fields: buildAddressFields({ prefix: MAIN_ADDRESS_PREFIX }),
        },
    ],
};
export var BILLING_ADDRESS_FIELD_NAMES = buildAddressFields({
    prefix: BILLING_ADDRESS_PREFIX,
}).map(function (field) { return field.name; });
var billingAddressSection = {
    id: 'billingAddress',
    title: 'Billing Address',
    configSection: 'Billing Address',
    variant: FormSectionVariant.Filled,
    groups: [
        {
            id: 'billingSameAsMainGroup',
            configSection: '',
            fields: [
                {
                    name: CUSTOMER_FIELD.BillingSameAsMain,
                    label: 'Check if same as main address above',
                    type: FormFieldType.CheckBox,
                    gridSize: FULL_WIDTH,
                    resetFields: BILLING_ADDRESS_FIELD_NAMES,
                },
            ],
        },
        {
            id: 'billingAddressFields',
            isVisible: function (values) { return !isBillingSameAsMain(values); },
            dependsOn: [CUSTOMER_FIELD.BillingSameAsMain],
            fields: buildAddressFields({
                prefix: BILLING_ADDRESS_PREFIX,
                apiPrefix: BILLING_API_PREFIX,
                addressLabel: 'Address',
                emailTooltip: 'Invoices and billing notices are sent to this address.',
                isDisabled: isBillingSameAsMain,
                isVisible: function (values) { return !isBillingSameAsMain(values); },
                dependsOn: [CUSTOMER_FIELD.BillingSameAsMain],
            }),
        },
    ],
};
var buildAuthorizedSignerSection = function (isCanadaProgram, collectsGuarantor) { return ({
    id: CUSTOMER_SECTION_ID.AuthorizedSigner,
    title: 'Authorized Document Signer',
    configSection: '',
    variant: FormSectionVariant.Filled,
    groups: [
        {
            id: 'authorizedSignerDetails',
            configSection: 'Individual Details',
            fields: [
                {
                    name: CUSTOMER_FIELD.SignerFirstName,
                    apiFieldName: API_FIELD.FirstName,
                    label: 'First Name',
                    isDesignOwned: true,
                    type: FormFieldType.Text,
                    isMandatory: true,
                    maxLength: 16,
                    format: sanitizeNameInput,
                    validate: function (value) { return firstNameOrLastNameValidation(value, 'First Name'); },
                    isVisible: function (values) { return !isSoleProprietorshipEntityType(values); },
                    dependsOn: [CUSTOMER_FIELD.EntityType],
                },
                {
                    name: CUSTOMER_FIELD.SignerMiddleName,
                    apiFieldName: API_FIELD.MiddleName,
                    label: 'Middle Name',
                    isDesignOwned: true,
                    type: FormFieldType.Text,
                    isMandatory: false,
                    maxLength: 16,
                    format: sanitizeNameInput,
                    isVisible: function (values) { return !isSoleProprietorshipEntityType(values); },
                    dependsOn: [CUSTOMER_FIELD.EntityType],
                },
                {
                    name: CUSTOMER_FIELD.SignerLastName,
                    apiFieldName: API_FIELD.LastName,
                    label: 'Last Name',
                    isDesignOwned: true,
                    type: FormFieldType.Text,
                    isMandatory: true,
                    maxLength: 16,
                    format: sanitizeNameInput,
                    validate: function (value) { return firstNameOrLastNameValidation(value, 'Last Name'); },
                    isVisible: function (values) { return !isSoleProprietorshipEntityType(values); },
                    dependsOn: [CUSTOMER_FIELD.EntityType],
                },
                {
                    name: CUSTOMER_FIELD.SignerSuffix,
                    apiFieldName: API_FIELD.Suffix,
                    label: 'Suffix',
                    isVisible: function (values) { return !isSoleProprietorshipEntityType(values); },
                    dependsOn: [CUSTOMER_FIELD.EntityType],
                    isDesignOwned: true,
                    type: FormFieldType.Select,
                    isMandatory: false,
                    options: SUFFIX_SELECT_OPTIONS,
                },
                {
                    name: CUSTOMER_FIELD.SignerTitle,
                    label: 'Title',
                    type: FormFieldType.Select,
                    isMandatory: true,
                    isDesignOwned: true,
                    dependsOn: [CUSTOMER_FIELD.EntityType],
                    getOptions: function (values) { return getTitleOptions(values[CUSTOMER_FIELD.EntityType]); },
                    isDisabled: function (values) { return !values[CUSTOMER_FIELD.EntityType]; },
                    disabledTooltip: 'Select Entity Type to enable Title',
                },
                {
                    name: CUSTOMER_FIELD.SignerEmail,
                    label: 'Email',
                    type: FormFieldType.Text,
                    isMandatory: true,
                    isDesignOwned: true,
                    maxLength: 100,
                    validate: function (value) { return emailValidation(value); },
                },
                {
                    name: CUSTOMER_FIELD.SignerPhone,
                    label: 'Phone Number',
                    type: FormFieldType.Text,
                    isMandatory: true,
                    isDesignOwned: true,
                    maxLength: 14,
                    format: formatPhoneInput,
                    validate: function (value) { return phoneNumberValidation(value); },
                },
            ],
        },
        {
            id: 'documentSignerFlag',
            configSection: '',
            isVisible: function () { return collectsGuarantor; },
            variant: FormGroupVariant.Highlighted,
            fields: [
                {
                    name: CUSTOMER_FIELD.DocumentSignerSameAsGuarantor,
                    label: 'Will the above Document Signer also be the Guarantor?',
                    type: FormFieldType.Radio,
                    isMandatory: true,
                    options: YES_NO_RADIO_OPTIONS,
                    isInlineLabel: true,
                    gridSize: FULL_WIDTH,
                },
            ],
        },
        {
            id: CUSTOMER_SECTION_ID.GuarantorAdditionalDetails,
            title: 'Guarantor Additional Details',
            configSection: 'Individual Details',
            isVisible: function (values) { return collectsGuarantor && isDocumentSignerAlsoGuarantor(values); },
            dependsOn: [CUSTOMER_FIELD.DocumentSignerSameAsGuarantor],
            fields: [
                {
                    name: CUSTOMER_FIELD.PercentOwnership,
                    label: 'Percent Ownership',
                    type: FormFieldType.Text,
                    isMandatory: true,
                    isDesignOwned: true,
                    maxLength: 6,
                    format: formatPercentageInput,
                    validate: percentageValidation,
                },
                {
                    name: CUSTOMER_FIELD.BusinessTitle,
                    apiFieldName: API_FIELD.BusinessTitle,
                    label: 'Business Title',
                    type: FormFieldType.Text,
                    isMandatory: false,
                    isDesignOwned: true,
                    maxLength: 50,
                    isVisible: function (values) { return !isSoleProprietorshipEntityType(values); },
                    dependsOn: [CUSTOMER_FIELD.EntityType],
                },
                {
                    name: CUSTOMER_FIELD.TaxpayerId,
                    apiFieldName: API_FIELD.TaxpayerId,
                    label: 'SSN#/TIN#/SIN#',
                    type: FormFieldType.Text,
                    isMandatory: !isCanadaProgram,
                    isDesignOwned: true,
                    maxLength: 11,
                    lookupKey: CUSTOMER_LOOKUP_KEY.ExistingSignerGuarantorByTaxId,
                    isVisible: function (values) { return !isSoleProprietorshipEntityType(values); },
                    dependsOn: [CUSTOMER_FIELD.Country, CUSTOMER_FIELD.EntityType],
                    format: function (value, values) {
                        return isCanadianCustomer(values, isCanadaProgram)
                            ? formatSINInput(value)
                            : formatSSNInput(value);
                    },
                    validate: function (value, values) {
                        return isCanadianCustomer(values, isCanadaProgram)
                            ? sinNumberValidation(value)
                            : ssnNumberValidation(value);
                    },
                },
                {
                    name: CUSTOMER_FIELD.DateOfBirth,
                    apiFieldName: API_FIELD.DateOfBirth,
                    label: 'Date Of Birth',
                    type: FormFieldType.Date,
                    isMandatory: true,
                    isDesignOwned: true,
                    maxDate: TODAY,
                    validate: function (value) { return dateOfBirthRangeValidation(value); },
                    isVisible: function (values) { return !isSoleProprietorshipEntityType(values); },
                    dependsOn: [CUSTOMER_FIELD.EntityType],
                },
            ],
        },
        {
            id: 'guarantorAddressDetails',
            title: 'Address Details',
            isVisible: function (values) { return collectsGuarantor && isDocumentSignerAlsoGuarantor(values); },
            dependsOn: [CUSTOMER_FIELD.DocumentSignerSameAsGuarantor],
            fields: buildAddressFields({
                prefix: GUARANTOR_ADDRESS_PREFIX,
                includeContact: false,
            }),
        },
    ],
}); };
export var SIGNER_GUARANTOR_FIELD_NAMES = __spreadArray([
    CUSTOMER_FIELD.PercentOwnership,
    CUSTOMER_FIELD.BusinessTitle,
    CUSTOMER_FIELD.TaxpayerId,
    CUSTOMER_FIELD.DateOfBirth
], buildAddressFields({ prefix: GUARANTOR_ADDRESS_PREFIX, includeContact: false }).map(function (field) { return field.name; }), true);
export var SIGNER_GUARANTOR_ALWAYS_EDITABLE_FIELD_NAMES = __spreadArray([
    CUSTOMER_FIELD.PercentOwnership
], buildAddressFields({ prefix: GUARANTOR_ADDRESS_PREFIX, includeContact: false }).map(function (field) { return field.name; }), true);
export var getCustomerRecordLabel = function (recordIndex) {
    if (recordIndex === 0) {
        return 'Primary Customer';
    }
    if (recordIndex === 1) {
        return 'Secondary Customer';
    }
    return "Customer ".concat(recordIndex + 1);
};
export var getGuarantorRecordLabel = function (recordIndex) {
    return recordIndex === 0 ? 'Primary Guarantor' : "Additional Guarantor ".concat(recordIndex);
};
export var buildCustomerSections = function (isPrimaryCustomer, isCanadaProgram, collectsGuarantor, isConsumerProgram) {
    if (isCanadaProgram === void 0) { isCanadaProgram = false; }
    if (collectsGuarantor === void 0) { collectsGuarantor = true; }
    if (isConsumerProgram === void 0) { isConsumerProgram = false; }
    return __spreadArray(__spreadArray([], (isConsumerProgram
        ? [buildIndividualDetailsSection(isCanadaProgram, undefined, isPrimaryCustomer)]
        : [
            buildIndividualDetailsSection(isCanadaProgram, isSoleProprietorshipEntityType, isPrimaryCustomer),
        ]), true), [
        buildBusinessDetailsSection(isPrimaryCustomer, isCanadaProgram, isConsumerProgram),
        addressDetailsSection,
        billingAddressSection,
        buildAuthorizedSignerSection(isCanadaProgram, collectsGuarantor),
    ], false);
};
var CUSTOMER_INFORMATION_SECTIONS = buildCustomerSections(true);
var BILLING_MIRROR_PAIRS = BILLING_ADDRESS_FIELD_NAMES.map(function (billingFieldName) { return [billingFieldName, billingFieldName.replace(BILLING_ADDRESS_PREFIX, MAIN_ADDRESS_PREFIX)]; });
export var deriveCustomerValues = function (values) {
    if (!isBillingSameAsMain(values)) {
        return {};
    }
    var mirrored = {};
    BILLING_MIRROR_PAIRS.forEach(function (_a) {
        var _b;
        var billingFieldName = _a[0], mainFieldName = _a[1];
        mirrored[billingFieldName] = (_b = values[mainFieldName]) !== null && _b !== void 0 ? _b : '';
    });
    return mirrored;
};
export var GUARANTOR_TYPE = {
    Individual: 'I',
    Business: 'B',
};
export var GUARANTOR_TYPE_OPTIONS = [
    { label: 'Individual', value: GUARANTOR_TYPE.Individual },
    { label: 'Business', value: GUARANTOR_TYPE.Business },
];
export var getGuarantorTypeLabel = function (guarantorType) { var _a, _b; return (_b = (_a = GUARANTOR_TYPE_OPTIONS.find(function (option) { return option.value === guarantorType; })) === null || _a === void 0 ? void 0 : _a.label) !== null && _b !== void 0 ? _b : ''; };
export var GUARANTOR_FIELD = {
    GuarantorType: 'guarantorType',
    FirstName: 'guarantorFirstName',
    MiddleName: 'guarantorMiddleName',
    LastName: 'guarantorLastName',
    Suffix: 'guarantorSuffix',
    TaxpayerId: 'guarantorTaxpayerId',
    DateOfBirth: 'guarantorDateOfBirth',
    BusinessName: 'guarantorBusinessName',
    BusinessFedTaxId: 'guarantorBusinessFedTaxId',
    BusinessEntityType: 'guarantorBusinessEntityType',
    BusinessTitle: 'guarantorBusinessTitle',
    HasOwnership: 'guarantorHasOwnership',
    OwnershipPercentage: 'guarantorOwnershipPercentage',
    SignerFirstName: 'guarantorSignerFirstName',
    SignerMiddleName: 'guarantorSignerMiddleName',
    SignerLastName: 'guarantorSignerLastName',
    SignerSuffix: 'guarantorSignerSuffix',
    SignerTitle: 'guarantorSignerTitle',
    SignerEmail: 'guarantorSignerEmail',
    SignerPhone: 'guarantorSignerPhone',
    CustomerAssociation: 'guarantorCustomerAssociation',
};
export var GUARANTOR_ASPIRE_FIELD = {
    GuarantorAspireOId: 'guarantorAspireOId',
    GuarantorAspireRecordId: 'guarantorAspireRecordId',
    LocationAspireOId: 'guarantorLocationAspireOId',
    LocationAspireRecordId: 'guarantorLocationAspireRecordId',
    SignerContactAspireOId: 'guarantorSignerContactAspireOId',
};
export var SIGNER_GUARANTOR_MATCHED_CONTACT_FIELD = {
    FirstName: 'signerGuarantorMatchedFirstName',
    LastName: 'signerGuarantorMatchedLastName',
    Email: 'signerGuarantorMatchedEmail',
    PhoneNumber: 'signerGuarantorMatchedPhoneNumber',
};
export var GUARANTOR_HOME_ADDRESS_PREFIX = 'guarantorHome';
export var GUARANTOR_ALWAYS_EDITABLE_FIELD_NAMES = [
    GUARANTOR_FIELD.OwnershipPercentage,
];
export var GUARANTOR_RESET_ON_TYPE_CHANGE_FIELD_NAMES = __spreadArray(__spreadArray(__spreadArray([], Object.values(GUARANTOR_FIELD).filter(function (fieldName) { return fieldName !== GUARANTOR_FIELD.GuarantorType; }), true), Object.values(GUARANTOR_ASPIRE_FIELD), true), buildAddressFields({
    prefix: GUARANTOR_HOME_ADDRESS_PREFIX,
    stateLabel: 'State/Province Of Incorporation',
}).map(function (field) { return field.name; }), true);
var GUARANTOR_HOME_COUNTRY_FIELD = "".concat(GUARANTOR_HOME_ADDRESS_PREFIX, "Country");
var isCanadianGuarantor = function (values) {
    return values[GUARANTOR_HOME_COUNTRY_FIELD] === CANADA_COUNTRY_CODE;
};
var hasGuarantorOwnership = function (values) {
    return Number(values[GUARANTOR_FIELD.HasOwnership]) === 1;
};
var isBusinessGuarantor = function (values) {
    return values[GUARANTOR_FIELD.GuarantorType] === GUARANTOR_TYPE.Business;
};
var isIndividualGuarantor = function (values) { return !isBusinessGuarantor(values); };
var guarantorTypeSection = {
    id: GUARANTOR_SECTION_ID.GuarantorType,
    configSection: '',
    variant: FormSectionVariant.Plain,
    groups: [
        {
            id: 'guarantorTypeFields',
            configSection: '',
            fields: [
                {
                    name: GUARANTOR_FIELD.GuarantorType,
                    label: 'Guarantor Type',
                    type: FormFieldType.Radio,
                    isMandatory: true,
                    options: GUARANTOR_TYPE_OPTIONS,
                    isInlineLabel: true,
                    defaultValue: GUARANTOR_TYPE.Individual,
                    gridSize: FULL_WIDTH,
                    resetFields: GUARANTOR_RESET_ON_TYPE_CHANGE_FIELD_NAMES,
                },
            ],
        },
    ],
};
var buildGuarantorCustomerAssociationField = function (customerOptions) {
    return customerOptions.length > 1
        ? [
            {
                name: GUARANTOR_FIELD.CustomerAssociation,
                label: 'Business Association',
                type: FormFieldType.Select,
                isMandatory: true,
                options: customerOptions,
            },
        ]
        : [];
};
var buildGuarantorIndividualSection = function (isCanadaProgram, customerOptions) {
    if (customerOptions === void 0) { customerOptions = []; }
    return ({
        id: GUARANTOR_SECTION_ID.IndividualDetails,
        title: 'Individual Details',
        configSection: 'Individual Details',
        variant: FormSectionVariant.Filled,
        groups: [
            {
                id: 'guarantorIndividualFields',
                configSection: 'Individual Details',
                fields: __spreadArray([
                    {
                        name: GUARANTOR_FIELD.FirstName,
                        apiFieldName: API_FIELD.FirstName,
                        label: 'First Name',
                        type: FormFieldType.Text,
                        isMandatory: true,
                        maxLength: 16,
                        format: sanitizeNameInput,
                        validate: function (value) { return firstNameOrLastNameValidation(value, 'First Name'); },
                    },
                    {
                        name: GUARANTOR_FIELD.MiddleName,
                        apiFieldName: API_FIELD.MiddleName,
                        label: 'Middle Name',
                        type: FormFieldType.Text,
                        isMandatory: false,
                        maxLength: 16,
                        format: sanitizeNameInput,
                    },
                    {
                        name: GUARANTOR_FIELD.LastName,
                        apiFieldName: API_FIELD.LastName,
                        label: 'Last Name',
                        type: FormFieldType.Text,
                        isMandatory: true,
                        maxLength: 16,
                        format: sanitizeNameInput,
                        validate: function (value) { return firstNameOrLastNameValidation(value, 'Last Name'); },
                    },
                    {
                        name: GUARANTOR_FIELD.Suffix,
                        apiFieldName: API_FIELD.Suffix,
                        label: 'Suffix',
                        type: FormFieldType.Select,
                        isMandatory: false,
                        options: SUFFIX_SELECT_OPTIONS,
                    },
                    {
                        name: GUARANTOR_FIELD.TaxpayerId,
                        apiFieldName: API_FIELD.TaxpayerId,
                        label: 'SSN#/TIN#/SIN#',
                        type: FormFieldType.Text,
                        isMandatory: !isCanadaProgram,
                        maxLength: 11,
                        lookupKey: GUARANTOR_LOOKUP_KEY.ExistingGuarantorByTaxId,
                        dependsOn: [GUARANTOR_HOME_COUNTRY_FIELD],
                        format: function (value, values) {
                            return isCanadianGuarantor(values) ? formatSINInput(value) : formatSSNInput(value);
                        },
                        validate: function (value, values) {
                            return isCanadianGuarantor(values)
                                ? sinNumberValidation(value)
                                : ssnNumberValidation(value);
                        },
                    },
                    {
                        name: GUARANTOR_FIELD.DateOfBirth,
                        apiFieldName: API_FIELD.DateOfBirth,
                        label: 'Date of Birth',
                        type: FormFieldType.Date,
                        isMandatory: true,
                        maxDate: TODAY,
                        validate: function (value) { return dateOfBirthRangeValidation(value); },
                    }
                ], buildGuarantorCustomerAssociationField(customerOptions), true),
            },
        ],
    });
};
var buildGuarantorBusinessSection = function (isCanadaProgram, customerOptions) {
    if (customerOptions === void 0) { customerOptions = []; }
    return ({
        id: GUARANTOR_SECTION_ID.BusinessDetails,
        title: 'Business Details',
        configSection: 'Business Details',
        variant: FormSectionVariant.Filled,
        groups: [
            {
                id: 'guarantorBusinessFields',
                configSection: 'Business Details',
                fields: __spreadArray([
                    {
                        name: GUARANTOR_FIELD.BusinessName,
                        apiFieldName: API_FIELD.BusinessName,
                        label: 'Business Name',
                        type: FormFieldType.Text,
                        isMandatory: true,
                        maxLength: 50,
                    },
                    {
                        name: GUARANTOR_FIELD.BusinessEntityType,
                        apiFieldName: API_FIELD.EntityType,
                        label: 'Company Type',
                        type: FormFieldType.Select,
                        isMandatory: true,
                        options: ENTITY_TYPE_OPTIONS,
                    },
                    {
                        name: GUARANTOR_FIELD.OwnershipPercentage,
                        apiFieldName: API_FIELD.OwnershipPercentage,
                        label: 'Percent Ownership%',
                        type: FormFieldType.Text,
                        isMandatory: true,
                        maxLength: 6,
                        format: formatPercentageInput,
                        validate: percentageValidation,
                    },
                    {
                        name: GUARANTOR_FIELD.BusinessFedTaxId,
                        apiFieldName: API_FIELD.TaxpayerId,
                        label: 'FED TAX ID/BN#',
                        type: FormFieldType.Text,
                        isMandatory: !isCanadaProgram,
                        maxLength: 15,
                        lookupKey: GUARANTOR_LOOKUP_KEY.ExistingGuarantorByTaxId,
                        dependsOn: [GUARANTOR_HOME_COUNTRY_FIELD],
                        format: formatTaxIdInput,
                        validate: function (value, values) {
                            return isCanadianGuarantor(values)
                                ? businessNumberValidation(value)
                                : federalTaxIdValidation(value);
                        },
                    }
                ], buildGuarantorCustomerAssociationField(customerOptions), true),
            },
        ],
    });
};
var guarantorSignerSection = {
    id: GUARANTOR_SECTION_ID.AuthorizedSigner,
    title: 'Authorized Document Signer',
    configSection: '',
    variant: FormSectionVariant.Filled,
    groups: [
        {
            id: 'guarantorSignerFields',
            configSection: '',
            fields: [
                {
                    name: GUARANTOR_FIELD.SignerFirstName,
                    label: 'First Name',
                    type: FormFieldType.Text,
                    isMandatory: true,
                    maxLength: 16,
                    format: sanitizeNameInput,
                    validate: function (value) { return firstNameOrLastNameValidation(value, 'First Name'); },
                },
                {
                    name: GUARANTOR_FIELD.SignerMiddleName,
                    label: 'Middle Name',
                    type: FormFieldType.Text,
                    maxLength: 16,
                    format: sanitizeNameInput,
                },
                {
                    name: GUARANTOR_FIELD.SignerLastName,
                    label: 'Last Name',
                    type: FormFieldType.Text,
                    isMandatory: true,
                    maxLength: 16,
                    format: sanitizeNameInput,
                    validate: function (value) { return firstNameOrLastNameValidation(value, 'Last Name'); },
                },
                {
                    name: GUARANTOR_FIELD.SignerSuffix,
                    label: 'Suffix',
                    type: FormFieldType.Select,
                    options: SUFFIX_SELECT_OPTIONS,
                },
                {
                    name: GUARANTOR_FIELD.SignerTitle,
                    label: 'Title',
                    type: FormFieldType.Select,
                    isMandatory: true,
                    dependsOn: [GUARANTOR_FIELD.BusinessEntityType],
                    getOptions: function (values) {
                        return getTitleOptions(values[GUARANTOR_FIELD.BusinessEntityType]);
                    },
                    isDisabled: function (values) { return !values[GUARANTOR_FIELD.BusinessEntityType]; },
                    disabledTooltip: 'Select Entity Type to enable Title',
                },
                {
                    name: GUARANTOR_FIELD.SignerEmail,
                    label: 'Email',
                    type: FormFieldType.Text,
                    isMandatory: true,
                    maxLength: 100,
                    validate: function (value) { return emailValidation(value); },
                },
                {
                    name: GUARANTOR_FIELD.SignerPhone,
                    label: 'Cell Phone Number',
                    type: FormFieldType.Text,
                    isMandatory: true,
                    maxLength: 14,
                    format: formatPhoneInput,
                    validate: function (value) { return phoneNumberValidation(value); },
                },
            ],
        },
    ],
};
var buildGuarantorOwnershipAndAddressSection = function () {
    return {
        id: GUARANTOR_SECTION_ID.OwnershipAndAddress,
        variant: FormSectionVariant.Filled,
        groups: [
            {
                id: 'guarantorOwnership',
                configSection: '',
                isVisible: isIndividualGuarantor,
                dependsOn: [GUARANTOR_FIELD.GuarantorType],
                variant: FormGroupVariant.Highlighted,
                fields: [
                    {
                        name: GUARANTOR_FIELD.HasOwnership,
                        label: 'Does the guarantor have ownership in the business?',
                        type: FormFieldType.Radio,
                        isMandatory: true,
                        options: YES_NO_RADIO_OPTIONS,
                        isInlineLabel: true,
                        gridSize: FULL_WIDTH,
                    },
                ],
            },
            {
                id: 'guarantorOwnershipPercentage',
                configSection: 'Individual Details',
                isVisible: function (values) {
                    return isIndividualGuarantor(values) && hasGuarantorOwnership(values);
                },
                dependsOn: [GUARANTOR_FIELD.GuarantorType, GUARANTOR_FIELD.HasOwnership],
                fields: [
                    {
                        name: GUARANTOR_FIELD.OwnershipPercentage,
                        apiFieldName: API_FIELD.OwnershipPercentage,
                        label: 'Percent Ownership%',
                        type: FormFieldType.Text,
                        isMandatory: true,
                        maxLength: 6,
                        format: formatPercentageInput,
                        validate: percentageValidation,
                        dependsOn: [GUARANTOR_FIELD.GuarantorType, GUARANTOR_FIELD.HasOwnership],
                        isVisible: function (values) {
                            return isIndividualGuarantor(values) && hasGuarantorOwnership(values);
                        },
                    },
                    {
                        name: GUARANTOR_FIELD.BusinessTitle,
                        apiFieldName: API_FIELD.BusinessTitle,
                        label: 'Business Title',
                        type: FormFieldType.Text,
                        isMandatory: false,
                        maxLength: 50,
                        dependsOn: [GUARANTOR_FIELD.GuarantorType, GUARANTOR_FIELD.HasOwnership],
                        isVisible: function (values) {
                            return isIndividualGuarantor(values) && hasGuarantorOwnership(values);
                        },
                    },
                ],
            },
            {
                id: 'guarantorHomeAddress',
                getTitle: function (values) {
                    return isIndividualGuarantor(values) ? 'Home Address' : 'Address Details';
                },
                dependsOn: [GUARANTOR_FIELD.GuarantorType],
                configSection: 'Home Address',
                fields: buildAddressFields({
                    prefix: GUARANTOR_HOME_ADDRESS_PREFIX,
                    stateLabel: 'State/Province Of Incorporation',
                }),
            },
        ],
    };
};
export var buildGuarantorSections = function (isCanadaProgram, customerOptions) {
    if (isCanadaProgram === void 0) { isCanadaProgram = false; }
    if (customerOptions === void 0) { customerOptions = []; }
    return [
        guarantorTypeSection,
        __assign(__assign({}, buildGuarantorIndividualSection(isCanadaProgram, customerOptions)), { isVisible: isIndividualGuarantor }),
        __assign(__assign({}, buildGuarantorBusinessSection(isCanadaProgram, customerOptions)), { isVisible: isBusinessGuarantor }),
        buildGuarantorOwnershipAndAddressSection(),
        __assign(__assign({}, guarantorSignerSection), { isVisible: isBusinessGuarantor }),
    ];
};
export var ASSET_FIELD = {
    Location: 'assetLocation',
    AddNewLocation: 'assetAddNewLocation',
    NewOrUsed: 'assetNewOrUsed',
    EquipmentType: 'assetEquipmentType',
    Description: 'assetDescription',
    VinLookup: 'assetVinLookup',
    Manufacturer: 'assetManufacturer',
    ListPrice: 'assetListPrice',
    Model: 'assetModel',
    Year: 'assetYear',
    SerialNumber: 'assetSerialNumber',
    CostPerUnit: 'assetCostPerUnit',
    Quantity: 'assetQuantity',
    IsTitled: 'assetIsTitled',
    Odometer: 'assetOdometer',
    ConversionCost: 'assetConversionCost',
    VASupport: 'assetVASupport',
};
var CONVERSION_COST_STATES = ['MD', 'OR', 'VA'];
export var ASSET_ACTION = {
    AddNewLocation: 'addNewLocation',
    VinLookup: 'vinLookup',
};
export var VIN_LENGTH = 17;
export var VIN_MIN_LOOKUP_LENGTH = 6;
export var formatVinInput = function (value) {
    return value.toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/g, '');
};
var NEW_OR_USED_OPTIONS = [
    { label: 'New', value: 'NEW' },
    { label: 'Used', value: 'USED' },
];
var readSerialNumberEntries = function (value) {
    return String(value !== null && value !== void 0 ? value : '')
        .split(',')
        .map(function (entry) { return entry.trim(); })
        .filter(Boolean);
};
export var buildAssetSections = function (equipmentTypeOptions, isVinDecoderEnabled, locationOptions, isPrimaryCustomerVeteran, primaryCustomerState) {
    if (equipmentTypeOptions === void 0) { equipmentTypeOptions = EQUIPMENT_TYPE_OPTIONS; }
    if (isVinDecoderEnabled === void 0) { isVinDecoderEnabled = false; }
    if (locationOptions === void 0) { locationOptions = LOCATION_OPTIONS; }
    if (isPrimaryCustomerVeteran === void 0) { isPrimaryCustomerVeteran = false; }
    if (primaryCustomerState === void 0) { primaryCustomerState = ''; }
    var showConversionCostField = CONVERSION_COST_STATES.includes(primaryCustomerState);
    var findType = function (values) {
        return equipmentTypeOptions.find(function (option) { var _a; return option.value === String((_a = values[ASSET_FIELD.EquipmentType]) !== null && _a !== void 0 ? _a : ''); });
    };
    var getManufacturerOptions = function (values) {
        var _a, _b;
        return ((_b = (_a = findType(values)) === null || _a === void 0 ? void 0 : _a.manufacturers) !== null && _b !== void 0 ? _b : []).map(function (manufacturer) { return ({
            label: manufacturer.name,
            value: manufacturer.name,
        }); });
    };
    var EQUIPMENT_CELL = isVinDecoderEnabled
        ? THIRD_WIDTH
        : { xs: 12, sm: 6, md: 3, lg: 3 };
    var getModelOptions = function (values) {
        var _a, _b, _c;
        var manufacturerName = String((_a = values[ASSET_FIELD.Manufacturer]) !== null && _a !== void 0 ? _a : '');
        var manufacturer = (_b = findType(values)) === null || _b === void 0 ? void 0 : _b.manufacturers.find(function (candidate) { return candidate.name === manufacturerName; });
        return ((_c = manufacturer === null || manufacturer === void 0 ? void 0 : manufacturer.models) !== null && _c !== void 0 ? _c : []).map(function (model) { return ({ label: model, value: model }); });
    };
    return [
        {
            id: 'assetDetails',
            configSection: 'Asset Information',
            variant: FormSectionVariant.Filled,
            groups: [
                {
                    id: 'assetLocationGroup',
                    configSection: '',
                    fields: [
                        {
                            name: ASSET_FIELD.Location,
                            label: 'Location',
                            type: FormFieldType.Select,
                            isMandatory: true,
                            options: locationOptions,
                            gridSize: { xs: 12, sm: 12, md: 8, lg: 8 },
                        },
                        {
                            name: ASSET_FIELD.AddNewLocation,
                            label: '+ Add New Location',
                            type: FormFieldType.Action,
                            actionKey: ASSET_ACTION.AddNewLocation,
                            gridSize: { xs: 12, sm: 12, md: 4, lg: 4 },
                        },
                    ],
                },
                {
                    id: 'assetConditionGroup',
                    fields: __spreadArray(__spreadArray([
                        {
                            name: ASSET_FIELD.NewOrUsed,
                            apiFieldName: 'IsNew',
                            label: 'New Or Used',
                            type: FormFieldType.Radio,
                            isMandatory: true,
                            options: NEW_OR_USED_OPTIONS,
                            labelPlacement: FormLabelPlacement.Above,
                            isBoxed: true,
                            gridSize: isVinDecoderEnabled ? THIRD_WIDTH : HALF_WIDTH,
                        },
                        {
                            name: ASSET_FIELD.EquipmentType,
                            apiFieldName: 'EquipmentType',
                            label: 'Equipment Type',
                            type: FormFieldType.Select,
                            isMandatory: true,
                            options: equipmentTypeOptions,
                            resetFields: [
                                ASSET_FIELD.Manufacturer,
                                ASSET_FIELD.Model,
                                ASSET_FIELD.Year,
                                ASSET_FIELD.Description,
                            ],
                            gridSize: EQUIPMENT_CELL,
                        }
                    ], (isVinDecoderEnabled
                        ? [{
                                name: ASSET_FIELD.VinLookup,
                                label: 'VIN Lookup',
                                type: FormFieldType.Action,
                                actionKey: ASSET_ACTION.VinLookup,
                                isPrimaryAction: true,
                                isCompactAction: true,
                                actionIcon: FormActionIcon.Search,
                                isDesignOwned: true,
                                gridSize: THIRD_WIDTH,
                            }]
                        : []), true), [
                        {
                            name: ASSET_FIELD.Description,
                            apiFieldName: 'Description',
                            label: 'Equipment Description',
                            type: FormFieldType.Text,
                            isMandatory: true,
                            maxLength: 100,
                            dependsOn: [ASSET_FIELD.EquipmentType],
                            isVisible: function (values) {
                                var _a;
                                return Boolean((_a = equipmentTypeOptions.find(function (option) {
                                    var _a;
                                    return option.value ===
                                        String((_a = values[ASSET_FIELD.EquipmentType]) !== null && _a !== void 0 ? _a : '');
                                })) === null || _a === void 0 ? void 0 : _a.isDescriptionFreeText);
                            },
                            gridSize: EQUIPMENT_CELL,
                        },
                        {
                            name: ASSET_FIELD.ListPrice,
                            apiFieldName: 'ListPrice',
                            label: 'MSRP',
                            type: FormFieldType.Text,
                            isMandatory: false,
                            isDisabled: function () { return true; },
                            isVisible: function (values) { return Boolean(values[ASSET_FIELD.ListPrice]); },
                            gridSize: EQUIPMENT_CELL,
                        },
                    ], false),
                },
                {
                    id: 'assetEquipmentGroup',
                    fields: [
                        {
                            name: ASSET_FIELD.Manufacturer,
                            apiFieldName: 'Manufacturer',
                            label: 'Equipment Manufacturer',
                            type: FormFieldType.AutoComplete,
                            isFreeSolo: true,
                            isMandatory: true,
                            dependsOn: [ASSET_FIELD.EquipmentType],
                            getOptions: getManufacturerOptions,
                            resetFields: [ASSET_FIELD.Model, ASSET_FIELD.Year],
                            isDisabled: function (values) { return !values[ASSET_FIELD.EquipmentType]; },
                            disabledTooltip: 'Select Equipment Type to enable this field',
                            gridSize: THIRD_WIDTH,
                        },
                        {
                            name: ASSET_FIELD.Year,
                            apiFieldName: 'Year',
                            label: 'Year',
                            type: FormFieldType.Text,
                            isMandatory: true,
                            maxLength: 4,
                            format: function (value) { return value.replace(/\D/g, ''); },
                            validate: yearValidation,
                            dependsOn: [ASSET_FIELD.EquipmentType],
                            isDisabled: function (values) { return !values[ASSET_FIELD.EquipmentType]; },
                            disabledTooltip: 'Select Equipment Type to enable this field',
                            gridSize: THIRD_WIDTH,
                        },
                        {
                            name: ASSET_FIELD.Model,
                            apiFieldName: 'Model',
                            label: 'Model',
                            type: FormFieldType.AutoComplete,
                            isFreeSolo: true,
                            isMandatory: false,
                            dependsOn: [ASSET_FIELD.EquipmentType, ASSET_FIELD.Manufacturer],
                            getOptions: getModelOptions,
                            isDisabled: function (values) { return !values[ASSET_FIELD.EquipmentType]; },
                            disabledTooltip: 'Select Equipment Type to enable this field',
                            gridSize: THIRD_WIDTH,
                        },
                        {
                            name: ASSET_FIELD.SerialNumber,
                            apiFieldName: 'SerialNums',
                            label: 'SN/VIN#',
                            type: FormFieldType.Text,
                            isMandatory: false,
                            maxLength: 25,
                            tooltip: 'Serial number for non-titled assets, VIN for titled assets.',
                            dependsOn: [ASSET_FIELD.IsTitled],
                            validate: function (value, values) {
                                if (!values[ASSET_FIELD.IsTitled]) {
                                    return '';
                                }
                                var entries = readSerialNumberEntries(value);
                                return entries.length > 0 &&
                                    entries.some(function (entry) { return entry.length < 6; })
                                    ? 'Enter a valid VIN (at least 6 characters) for each titled asset.'
                                    : '';
                            },
                            gridSize: THIRD_WIDTH,
                        },
                        {
                            name: ASSET_FIELD.CostPerUnit,
                            apiFieldName: API_FIELD.EquipmentCost,
                            label: 'Equipment Cost (Per Unit)',
                            type: FormFieldType.Text,
                            isMandatory: true,
                            maxLength: 16,
                            format: formatAmountInput,
                            validate: amountValidation,
                            gridSize: THIRD_WIDTH,
                        },
                        {
                            name: ASSET_FIELD.Quantity,
                            apiFieldName: API_FIELD.Quantity,
                            label: 'Quantity',
                            type: FormFieldType.Text,
                            isMandatory: true,
                            maxLength: 4,
                            format: function (value) { return value.replace(/\D/g, ''); },
                            dependsOn: [ASSET_FIELD.SerialNumber],
                            validate: function (value, values) {
                                var quantityError = quantityValidation(value);
                                if (quantityError) {
                                    return quantityError;
                                }
                                var serialEntries = readSerialNumberEntries(values[ASSET_FIELD.SerialNumber]);
                                return serialEntries.length > 0 &&
                                    serialEntries.length !== Number(value)
                                    ? 'The number of serial numbers entered must match the quantity.'
                                    : '';
                            },
                            gridSize: THIRD_WIDTH,
                        },
                        {
                            name: ASSET_FIELD.Odometer,
                            apiFieldName: 'Odometer',
                            label: 'Odometer',
                            type: FormFieldType.Text,
                            isMandatory: false,
                            maxLength: 8,
                            format: function (value) { return value.replace(/\D/g, ''); },
                            validate: function (value) {
                                return value !== '' && Number(value) < 0
                                    ? 'Odometer cannot be negative'
                                    : '';
                            },
                            gridSize: THIRD_WIDTH,
                        },
                        {
                            name: ASSET_FIELD.ConversionCost,
                            apiFieldName: 'ConversionCost',
                            label: 'Conversion Cost',
                            type: FormFieldType.Text,
                            isVisible: function () { return showConversionCostField; },
                            isMandatory: showConversionCostField,
                            maxLength: 16,
                            format: formatAmountInput,
                            dependsOn: [ASSET_FIELD.CostPerUnit],
                            validate: function (value, values) {
                                if (value === '') {
                                    return '';
                                }
                                var conversionCost = parseAmount(value);
                                var equipmentCost = parseAmount(values[ASSET_FIELD.CostPerUnit]);
                                return conversionCost > equipmentCost
                                    ? 'Conversion Cost cannot exceed the equipment cost'
                                    : '';
                            },
                            gridSize: THIRD_WIDTH,
                        },
                    ],
                },
                {
                    id: 'assetTitledGroup',
                    fields: [
                        {
                            name: ASSET_FIELD.IsTitled,
                            apiFieldName: 'IsTitledAsset',
                            label: 'Is this a Titled Asset?',
                            type: FormFieldType.CheckBox,
                            gridSize: FULL_WIDTH,
                        },
                        {
                            name: ASSET_FIELD.VASupport,
                            apiFieldName: 'VASupport',
                            label: 'VA Support',
                            type: FormFieldType.CheckBox,
                            isDesignOwned: true,
                            isVisible: function () { return isPrimaryCustomerVeteran; },
                            gridSize: FULL_WIDTH,
                        },
                    ],
                },
            ],
        },
    ];
};
export var buildAssetValueDerivation = function (equipmentTypeOptions) { return function (values) {
    var _a;
    var selectedType = equipmentTypeOptions.find(function (option) { var _a; return option.value === String((_a = values[ASSET_FIELD.EquipmentType]) !== null && _a !== void 0 ? _a : ''); });
    if (!selectedType || selectedType.isDescriptionFreeText) {
        return {};
    }
    return _a = {}, _a[ASSET_FIELD.Description] = selectedType.description, _a;
}; };
export var calculateTotalAssetCost = function (assets) {
    return assets.reduce(function (total, asset) {
        var _a;
        var unitCost = parseAmount(asset[ASSET_FIELD.CostPerUnit]);
        var quantity = Number((_a = asset[ASSET_FIELD.Quantity]) !== null && _a !== void 0 ? _a : 0);
        return total + unitCost * (Number.isFinite(quantity) ? quantity : 0);
    }, 0);
};
export var FINANCE_FIELD = {
    FinanceProduct: 'financeProduct',
    PricingOption: 'pricingOption',
    TermLengthMonths: 'termLengthMonths',
    DownPayment: 'downPayment',
    AdvancePaymentAmount: 'advancePaymentAmount',
    TotalAssetCost: 'totalAssetCost',
    TotalAmountFinanced: 'totalAmountFinanced',
    IsAssetForRental: 'isAssetForRental',
    TradeInAllowance: 'tradeInAllowance',
    Rebate: 'rebate',
    CashDown: 'cashDown',
    IsSeasonalPayment: 'isSeasonalPayment',
    DownPaymentAssistance: 'downPaymentAssistance',
    AmountRequested: 'amountRequested',
    PurposeOfFinance: 'purposeOfFinance',
};
export var FINANCE_SECTION_ID = {
    FinanceOptions: 'financeOptions',
};
var TERM_LENGTH_MONTHS = [12, 24, 36, 48, 60, 72, 84, 96, 108, 120];
var TERM_LENGTH_OPTIONS = TERM_LENGTH_MONTHS.map(function (months) { return ({
    label: String(months),
    value: months,
}); });
export var ASSET_LOCATION_TYPE = {
    Customer: 'CUSTOMER',
    Billing: 'BILLING',
    New: 'NEW',
};
export var describeAssetLocation = function (location) {
    return [
        location.locationName !== location.address ? location.locationName : '',
        location.address,
        location.city,
        location.postalCode,
        location.state,
        location.country,
    ]
        .filter(Boolean)
        .join(', ');
};
export var readCustomerAssetLocation = function (primaryCustomer, prefix) {
    var read = function (suffix) { var _a; return String((_a = primaryCustomer[buildAddressFieldName(prefix, suffix)]) !== null && _a !== void 0 ? _a : '').trim(); };
    var street = read('Address');
    return {
        locationName: street,
        address: street,
        city: read('City'),
        state: read('State'),
        country: read('Country'),
        postalCode: read('PostalCode'),
    };
};
export var buildAssetLocationChoices = function (primaryCustomer) {
    var choices = [];
    var customerLocation = readCustomerAssetLocation(primaryCustomer, MAIN_ADDRESS_PREFIX);
    var customerLabel = describeAssetLocation(customerLocation);
    if (customerLabel) {
        choices.push({
            label: customerLabel,
            locationType: ASSET_LOCATION_TYPE.Customer,
            location: customerLocation,
        });
    }
    if (!isBillingSameAsMain(primaryCustomer)) {
        var billingLocation = readCustomerAssetLocation(primaryCustomer, BILLING_ADDRESS_PREFIX);
        var billingLabel = describeAssetLocation(billingLocation);
        if (billingLabel && billingLabel !== customerLabel) {
            choices.push({
                label: billingLabel,
                locationType: ASSET_LOCATION_TYPE.Billing,
                location: billingLocation,
            });
        }
    }
    return choices;
};
export var toAssetLocationOptions = function (choices) {
    return choices.map(function (choice) { return ({ label: choice.label, value: choice.label }); });
};
var NEW_LOCATION_PREFIX = 'newLocation';
var NEW_LOCATION_FIELD = {
    LocationName: 'newLocationName',
    Address: buildAddressFieldName(NEW_LOCATION_PREFIX, 'Address'),
    Country: buildAddressFieldName(NEW_LOCATION_PREFIX, 'Country'),
    City: buildAddressFieldName(NEW_LOCATION_PREFIX, 'City'),
    State: buildAddressFieldName(NEW_LOCATION_PREFIX, 'State'),
    PostalCode: buildAddressFieldName(NEW_LOCATION_PREFIX, 'PostalCode'),
};
export var buildNewLocationSections = function () { return [
    {
        id: 'newLocationDetails',
        configSection: '',
        variant: FormSectionVariant.Plain,
        groups: [
            {
                id: 'newLocationFields',
                configSection: '',
                fields: __spreadArray([
                    {
                        name: NEW_LOCATION_FIELD.LocationName,
                        label: 'Location Name',
                        type: FormFieldType.Text,
                        isMandatory: true,
                        maxLength: 50,
                        gridSize: HALF_WIDTH,
                    }
                ], buildAddressFields({
                    prefix: NEW_LOCATION_PREFIX,
                    addressLabel: 'Address',
                    stateLabel: 'State',
                    includeContact: false,
                }).map(function (field) { return (__assign(__assign({}, field), { gridSize: HALF_WIDTH })); }), true),
            },
        ],
    },
]; };
export var readNewLocationValues = function (values) {
    var _a, _b, _c, _d, _e, _f;
    return ({
        locationName: String((_a = values[NEW_LOCATION_FIELD.LocationName]) !== null && _a !== void 0 ? _a : '').trim(),
        address: String((_b = values[NEW_LOCATION_FIELD.Address]) !== null && _b !== void 0 ? _b : '').trim(),
        country: String((_c = values[NEW_LOCATION_FIELD.Country]) !== null && _c !== void 0 ? _c : '').trim(),
        city: String((_d = values[NEW_LOCATION_FIELD.City]) !== null && _d !== void 0 ? _d : '').trim(),
        state: String((_e = values[NEW_LOCATION_FIELD.State]) !== null && _e !== void 0 ? _e : '').trim(),
        postalCode: String((_f = values[NEW_LOCATION_FIELD.PostalCode]) !== null && _f !== void 0 ? _f : '').trim(),
    });
};
export var buildFinanceAndDocumentsSections = function (financeProductOptions, isLineOfCredit, isConsumerProgram, pricingOptionsByProduct, vendorConfig, isPrimaryCustomerVeteran) {
    if (financeProductOptions === void 0) { financeProductOptions = FINANCE_PRODUCT_OPTIONS; }
    if (isLineOfCredit === void 0) { isLineOfCredit = false; }
    if (isConsumerProgram === void 0) { isConsumerProgram = false; }
    if (pricingOptionsByProduct === void 0) { pricingOptionsByProduct = {}; }
    if (vendorConfig === void 0) { vendorConfig = null; }
    if (isPrimaryCustomerVeteran === void 0) { isPrimaryCustomerVeteran = false; }
    var productsByCode = new Map(financeProductOptions.map(function (product) { return [product.value, product]; }));
    var isLoanProduct = function (values) {
        var _a;
        var product = productsByCode.get(String((_a = values[FINANCE_FIELD.FinanceProduct]) !== null && _a !== void 0 ? _a : ''));
        return product ? Boolean(product.isLoan) : true;
    };
    return [
        {
            id: FINANCE_SECTION_ID.FinanceOptions,
            configSection: isLineOfCredit ? 'Line of Credit Request' : 'Finance Options',
            variant: FormSectionVariant.Filled,
            groups: [
                {
                    id: 'financeProductGroup',
                    fields: [
                        {
                            name: FINANCE_FIELD.FinanceProduct,
                            apiFieldName: 'FinanceProductId',
                            label: 'Finance Product',
                            type: FormFieldType.Select,
                            isMandatory: true,
                            placeholder: 'Finance Product',
                            labelPlacement: FormLabelPlacement.Above,
                            resetFields: [FINANCE_FIELD.TermLengthMonths],
                            options: financeProductOptions,
                            gridSize: THIRD_WIDTH,
                        },
                    ],
                },
                {
                    id: 'financeLineOfCreditGroup',
                    isVisible: function () { return isLineOfCredit; },
                    fields: [
                        {
                            name: FINANCE_FIELD.AmountRequested,
                            apiFieldName: 'AmountRequested',
                            label: 'Amount Requested',
                            type: FormFieldType.Text,
                            isMandatory: true,
                            isDesignOwned: true,
                            maxLength: 16,
                            placeholder: '$0.00',
                            labelPlacement: FormLabelPlacement.Above,
                            format: formatAmountInput,
                            gridSize: THIRD_WIDTH,
                        },
                        {
                            name: FINANCE_FIELD.PurposeOfFinance,
                            apiFieldName: 'PurposeOfFinance',
                            label: 'Purpose of Finance',
                            type: FormFieldType.Text,
                            isMandatory: true,
                            isDesignOwned: true,
                            maxLength: 200,
                            placeholder: 'Enter Text',
                            labelPlacement: FormLabelPlacement.Above,
                            gridSize: THIRD_WIDTH,
                        },
                    ],
                },
                {
                    id: 'financeTermGroup',
                    isVisible: function () { return !isLineOfCredit; },
                    fields: [
                        {
                            name: FINANCE_FIELD.TermLengthMonths,
                            apiFieldName: 'Term',
                            label: 'Term Length in Months',
                            type: FormFieldType.ChipSelect,
                            isMandatory: true,
                            options: TERM_LENGTH_OPTIONS,
                            labelPlacement: FormLabelPlacement.Above,
                            gridSize: FULL_WIDTH,
                        },
                    ],
                },
                {
                    id: 'financeAmountsGroup',
                    isVisible: function () { return !isLineOfCredit; },
                    fields: [
                        {
                            name: FINANCE_FIELD.DownPayment,
                            apiFieldName: API_FIELD.DownPayment,
                            label: 'Down Payment',
                            type: FormFieldType.Text,
                            isMandatory: true,
                            maxLength: 16,
                            placeholder: '$0.00',
                            format: formatAmountInput,
                            dependsOn: [FINANCE_FIELD.TotalAssetCost, FINANCE_FIELD.FinanceProduct],
                            isVisible: function (values) { return isLoanProduct(values); },
                            validate: function (value, values) {
                                return parseAmount(value) > parseAmount(values[FINANCE_FIELD.TotalAssetCost])
                                    ? 'Down Payment cannot exceed the total asset cost'
                                    : '';
                            },
                            gridSize: THIRD_WIDTH,
                        },
                        {
                            name: FINANCE_FIELD.AdvancePaymentAmount,
                            apiFieldName: 'AdvancePaymentAmount',
                            label: 'Advance Payment',
                            type: FormFieldType.Text,
                            isMandatory: true,
                            maxLength: 16,
                            placeholder: '$0.00',
                            format: formatAmountInput,
                            dependsOn: [FINANCE_FIELD.TotalAssetCost, FINANCE_FIELD.FinanceProduct],
                            isVisible: function (values) { return !isLoanProduct(values); },
                            validate: function (value, values) {
                                return parseAmount(value) > parseAmount(values[FINANCE_FIELD.TotalAssetCost])
                                    ? 'Advance Payment cannot exceed the total asset cost'
                                    : '';
                            },
                            gridSize: THIRD_WIDTH,
                        },
                        {
                            name: FINANCE_FIELD.TotalAssetCost,
                            apiFieldName: API_FIELD.TotalAssetCost,
                            label: 'Total Asset Cost',
                            type: FormFieldType.Text,
                            isDisabled: function () { return true; },
                            gridSize: THIRD_WIDTH,
                        },
                        {
                            name: FINANCE_FIELD.TotalAmountFinanced,
                            apiFieldName: API_FIELD.TotalAmountFinanced,
                            label: 'Total Amount Financed',
                            type: FormFieldType.Text,
                            isDisabled: function () { return true; },
                            gridSize: THIRD_WIDTH,
                        },
                    ],
                },
                {
                    id: 'financeConsumerLeaseGroup',
                    isVisible: function (values) {
                        return !isLineOfCredit && isConsumerProgram && !isLoanProduct(values);
                    },
                    dependsOn: [FINANCE_FIELD.FinanceProduct],
                    fields: [
                        {
                            name: FINANCE_FIELD.TradeInAllowance,
                            apiFieldName: 'TradeInAllowance',
                            label: 'Trade-In Allowance',
                            type: FormFieldType.Text,
                            isMandatory: false,
                            maxLength: 16,
                            placeholder: '$0.00',
                            format: formatAmountInput,
                            gridSize: THIRD_WIDTH,
                        },
                        {
                            name: FINANCE_FIELD.Rebate,
                            apiFieldName: 'Rebate',
                            label: 'Rebate',
                            type: FormFieldType.Text,
                            isMandatory: false,
                            maxLength: 16,
                            placeholder: '$0.00',
                            format: formatAmountInput,
                            gridSize: THIRD_WIDTH,
                        },
                        {
                            name: FINANCE_FIELD.CashDown,
                            apiFieldName: 'CashDown',
                            label: 'Cash Down',
                            type: FormFieldType.Text,
                            isMandatory: false,
                            maxLength: 16,
                            placeholder: '$0.00',
                            format: formatAmountInput,
                            gridSize: THIRD_WIDTH,
                        },
                    ],
                },
                {
                    id: 'financeRentalGroup',
                    isVisible: function () { return !isLineOfCredit && !isConsumerProgram; },
                    fields: [
                        {
                            name: FINANCE_FIELD.IsAssetForRental,
                            apiFieldName: 'IsRental',
                            label: 'Is the asset being used for Rental purposes?',
                            type: FormFieldType.Radio,
                            isMandatory: true,
                            options: YES_NO_RADIO_OPTIONS,
                            labelPlacement: FormLabelPlacement.Above,
                            gridSize: FULL_WIDTH,
                        },
                    ],
                },
                {
                    id: 'financeVendorOptionsGroup',
                    configSection: '',
                    isVisible: function () {
                        return !isLineOfCredit &&
                            (Boolean(vendorConfig === null || vendorConfig === void 0 ? void 0 : vendorConfig.IsSeasonalPayment) || isPrimaryCustomerVeteran);
                    },
                    fields: [
                        {
                            name: FINANCE_FIELD.IsSeasonalPayment,
                            apiFieldName: 'IsSeasonalPayment',
                            label: 'Seasonal Payment Structure Desired?',
                            type: FormFieldType.CheckBox,
                            isVisible: function () { return Boolean(vendorConfig === null || vendorConfig === void 0 ? void 0 : vendorConfig.IsSeasonalPayment); },
                            gridSize: HALF_WIDTH,
                        },
                        {
                            name: FINANCE_FIELD.DownPaymentAssistance,
                            apiFieldName: 'DownPaymentAssistance',
                            label: 'Down Payment Assistance',
                            type: FormFieldType.CheckBox,
                            isVisible: function () { return isPrimaryCustomerVeteran; },
                            gridSize: HALF_WIDTH,
                        },
                    ],
                },
            ],
        },
    ];
};
export var deriveFinanceValues = function (values) {
    var _a;
    var totalAssetCost = parseAmount(values[FINANCE_FIELD.TotalAssetCost]);
    var amountPaidUpFront = parseAmount(values[FINANCE_FIELD.DownPayment]) +
        parseAmount(values[FINANCE_FIELD.AdvancePaymentAmount]);
    var amountFinanced = Math.max(totalAssetCost - amountPaidUpFront, 0);
    return _a = {},
        _a[FINANCE_FIELD.TotalAmountFinanced] = formatCurrencyDisplay(amountFinanced),
        _a;
};
export var CREATE_APPLICATION_STEP_ID = {
    CustomerInformation: 'customerInformation',
    GuarantorInformation: 'guarantorInformation',
    DocumentSignerInformation: 'documentSignerInformation',
    AssetInformation: 'assetInformation',
    FilesFinanceAndComments: 'filesFinanceAndComments',
};
var STEPS_EXCLUDED_BY_APPLICATION_TYPE = (_a = {},
    _a[APPLICATION_TYPES.LOC] = [CREATE_APPLICATION_STEP_ID.AssetInformation],
    _a[APPLICATION_TYPES.DORF] = [CREATE_APPLICATION_STEP_ID.AssetInformation],
    _a[APPLICATION_TYPES.SCH] = [CREATE_APPLICATION_STEP_ID.GuarantorInformation],
    _a);
export var collectsGuarantorForApplicationType = function (applicationType) {
    var _a;
    return !((_a = STEPS_EXCLUDED_BY_APPLICATION_TYPE[applicationType]) !== null && _a !== void 0 ? _a : []).includes(CREATE_APPLICATION_STEP_ID.GuarantorInformation);
};
var COMMERCIAL_ONLY_STEPS = [
    CREATE_APPLICATION_STEP_ID.GuarantorInformation,
    CREATE_APPLICATION_STEP_ID.DocumentSignerInformation,
];
var CONSUMER_PROGRAM_MARKER = 'consumer';
export var isConsumerProgramName = function (programName) {
    return (programName !== null && programName !== void 0 ? programName : '').toLowerCase().includes(CONSUMER_PROGRAM_MARKER);
};
export var areAllCustomersSoleProprietorships = function (customers) {
    return customers.length > 0 &&
        customers.every(function (customer) {
            var _a;
            return String((_a = customer[CUSTOMER_FIELD.EntityType]) !== null && _a !== void 0 ? _a : '') ===
                SOLE_PROPRIETORSHIP_ENTITY_CODE;
        });
};
var COMMERCIAL_ONLY_SECTION_IDS = [CUSTOMER_SECTION_ID.AuthorizedSigner];
export var isSectionInApplicationType = function (sectionId, _a) {
    var programName = _a.programName, _b = _a.areAllCustomersSoleProp, areAllCustomersSoleProp = _b === void 0 ? false : _b;
    if (!COMMERCIAL_ONLY_SECTION_IDS.includes(sectionId)) {
        return true;
    }
    var isConsumerProgram = String(programName !== null && programName !== void 0 ? programName : '')
        .toLowerCase()
        .includes(CONSUMER_PROGRAM_MARKER);
    return !isConsumerProgram && !areAllCustomersSoleProp;
};
export var isStepInApplicationType = function (stepId, _a) {
    var _b;
    var applicationType = _a.applicationType, programName = _a.programName, areAllCustomersSoleProp = _a.areAllCustomersSoleProp;
    if (((_b = STEPS_EXCLUDED_BY_APPLICATION_TYPE[applicationType]) !== null && _b !== void 0 ? _b : []).includes(stepId)) {
        return false;
    }
    if (!COMMERCIAL_ONLY_STEPS.includes(stepId)) {
        return true;
    }
    var isConsumerProgram = programName.toLowerCase().includes(CONSUMER_PROGRAM_MARKER);
    return !isConsumerProgram && !areAllCustomersSoleProp;
};
var LAST_STEP_LABEL_BY_APPLICATION_TYPE = (_b = {},
    _b[APPLICATION_TYPES.DORF] = 'Files Upload, Dealer Operated Rental Fleet & Comments',
    _b[APPLICATION_TYPES.LOC] = 'Files Upload, Line Of Credit Request & Comments',
    _b);
export var getStepLabel = function (step, applicationType) {
    var _a;
    return step.id === CREATE_APPLICATION_STEP_ID.FilesFinanceAndComments
        ? (_a = LAST_STEP_LABEL_BY_APPLICATION_TYPE[applicationType]) !== null && _a !== void 0 ? _a : step.label
        : step.label;
};
export var CREATE_APPLICATION_STEPS = [
    {
        id: CREATE_APPLICATION_STEP_ID.CustomerInformation,
        label: 'Customer Information',
        sections: CUSTOMER_INFORMATION_SECTIONS,
    },
    {
        id: CREATE_APPLICATION_STEP_ID.GuarantorInformation,
        label: 'Guarantor Information',
        sections: buildGuarantorSections(),
    },
    {
        id: CREATE_APPLICATION_STEP_ID.DocumentSignerInformation,
        label: 'Document Signer Information',
        sections: [],
    },
    {
        id: CREATE_APPLICATION_STEP_ID.AssetInformation,
        label: 'Asset Information',
        sections: buildAssetSections(),
    },
    {
        id: CREATE_APPLICATION_STEP_ID.FilesFinanceAndComments,
        label: 'Files Upload, Finance Options & Comments',
        sections: buildFinanceAndDocumentsSections(),
    },
];
